import React, { useState, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useTableView } from "@/hooks/useTableView";
import DataTable from "./table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  ArrowUpDown,
} from "lucide-react";

const ListView = observer(
  ({
    data,
    isLoading,
    columns,
    searchParams,
    renderGridItem,
    enablePagination = true,
    itemsPerPage = 10,
    currentPage = 1,
    totalItems = 0,
    handleSearch,
    onPageChange,
    handleSort,
    renderSearchInputs,
    onRowClick,
    columnVisibilityKey,
    defaultVisibleColumnKeys,
  }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const { isTableView } = useTableView();

    const allColumnKeys = useMemo(
      () => (columns ? columns.map((column) => column.key) : []),
      [columns]
    );
    const columnVisibilityEnabled = Boolean(columnVisibilityKey);

    const getInitialVisibleColumnKeys = () => {
      if (!columnVisibilityEnabled) {
        return allColumnKeys;
      }

      if (typeof window !== "undefined") {
        const storedValue = window.localStorage.getItem(columnVisibilityKey);
        if (storedValue) {
          try {
            const parsed = JSON.parse(storedValue);
            if (Array.isArray(parsed) && parsed.length > 0) {
              const sanitized = parsed.filter((key) => allColumnKeys.includes(key));
              if (sanitized.length > 0) {
                return sanitized;
              }
            }
          } catch (error) {
            console.error("Failed to parse stored column preferences", error);
          }
        }
      }

      if (
        Array.isArray(defaultVisibleColumnKeys) &&
        defaultVisibleColumnKeys.length > 0
      ) {
        const sanitizedDefaults = defaultVisibleColumnKeys.filter((key) =>
          allColumnKeys.includes(key)
        );
        if (sanitizedDefaults.length > 0) {
          return sanitizedDefaults;
        }
      }

      return allColumnKeys;
    };

    const [visibleColumnKeys, setVisibleColumnKeys] = useState(
      getInitialVisibleColumnKeys
    );
    const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
    const columnSelectorRef = useRef(null);

    useEffect(() => {
      if (!columnVisibilityEnabled) {
        setVisibleColumnKeys(allColumnKeys);
        return;
      }

      setVisibleColumnKeys((prevKeys) => {
        const sanitizedPrev = prevKeys.filter((key) => allColumnKeys.includes(key));
        const missingKeys = allColumnKeys.filter(
          (key) => !sanitizedPrev.includes(key)
        );
        const nextKeys = [...sanitizedPrev, ...missingKeys];
        return nextKeys.length > 0 ? nextKeys : allColumnKeys;
      });
    }, [allColumnKeys, columnVisibilityEnabled]);

    useEffect(() => {
      if (!isColumnSelectorOpen) {
        return;
      }

      const handleClickOutside = (event) => {
        if (
          columnSelectorRef.current &&
          !columnSelectorRef.current.contains(event.target)
        ) {
          setIsColumnSelectorOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isColumnSelectorOpen]);

    useEffect(() => {
      if (!columnVisibilityEnabled || typeof window === "undefined") {
        return;
      }

      window.localStorage.setItem(
        columnVisibilityKey,
        JSON.stringify(visibleColumnKeys)
      );
    }, [columnVisibilityEnabled, columnVisibilityKey, visibleColumnKeys]);

    const toggleColumnVisibility = (columnKey) => {
      if (!columnVisibilityEnabled) {
        return;
      }

      setVisibleColumnKeys((prevKeys) => {
        const isVisible = prevKeys.includes(columnKey);
        if (isVisible) {
          if (prevKeys.length === 1) {
            return prevKeys;
          }
          return prevKeys.filter((key) => key !== columnKey);
        }
        return [...prevKeys, columnKey];
      });
    };

    const handleSelectAllColumns = () => {
      if (!columnVisibilityEnabled) {
        return;
      }
      setVisibleColumnKeys(allColumnKeys);
    };

    const handleResetColumns = () => {
      if (!columnVisibilityEnabled) {
        return;
      }
      if (
        Array.isArray(defaultVisibleColumnKeys) &&
        defaultVisibleColumnKeys.length > 0
      ) {
        const sanitizedDefaults = defaultVisibleColumnKeys.filter((key) =>
          allColumnKeys.includes(key)
        );
        if (sanitizedDefaults.length > 0) {
          setVisibleColumnKeys(sanitizedDefaults);
          return;
        }
      }
      setVisibleColumnKeys(allColumnKeys);
    };

    const displayedColumns = useMemo(() => {
      if (!columnVisibilityEnabled) {
        return columns;
      }
      return columns.filter((column) => visibleColumnKeys.includes(column.key));
    }, [columnVisibilityEnabled, columns, visibleColumnKeys]);

    const pageCount = Math.ceil(totalItems / itemsPerPage);
    if (isLoading) return <div>Loading...</div>;

    return (
      <div>
        {isTableView || !renderGridItem ? (
          <>
            {columnVisibilityEnabled && columns.length > 0 && (
              <div className="flex justify-end mb-2">
                <div className="relative" ref={columnSelectorRef}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setIsColumnSelectorOpen((prevState) => !prevState)
                    }
                  >
                    Columns
                  </Button>
                  {isColumnSelectorOpen && (
                    <div className="absolute right-0 z-20 mt-2 w-64 rounded-md border bg-card shadow-lg">
                      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
                        <span className="text-sm font-medium">Visible Columns</span>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={handleResetColumns}
                          >
                            Reset
                          </Button>
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={handleSelectAllColumns}
                          >
                            All
                          </Button>
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto px-3 py-2">
                        {columns.map((column) => (
                          <label
                            key={column.key}
                            className="flex items-center gap-2 py-1 text-sm"
                          >
                            <Checkbox
                              checked={visibleColumnKeys.includes(column.key)}
                              onCheckedChange={() =>
                                toggleColumnVisibility(column.key)
                              }
                            />
                            <span className="truncate" title={column.label}>
                              {column.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <DataTable
              data={data}
              columns={displayedColumns}
              isLoading={isLoading}
              handleSort={handleSort}
              onRowClick={onRowClick}
            />
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {data.map((item) => renderGridItem && renderGridItem(item))}
          </div>
        )}
        {enablePagination && (
          <div className="flex  justify-end">
            <div className="flex items-center space-x-2 py-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span>
                {currentPage} / {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === pageCount}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(pageCount)}
                disabled={currentPage === pageCount}
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center  pl-4 space-x-2 py-4">
              Total: {totalItems}
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default ListView;
